import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntryInput, EntryUpdateInput } from '@redirector/shared';
import { apiGet, apiSend } from '../lib/api.js';

export type EntrySummary = {
  id: number;
  domainId: number;
  label: string;
  path: string;
  pathNorm: string;
  enabled: boolean;
  destinationCount: number;
  enabledDestinationCount: number;
  createdAt: string;
  updatedAt: string;
};

export type Destination = {
  id: number;
  url: string;
  weightBp: number;
  enabled: boolean;
  position: number;
};

export type DomainRecord = {
  id: number;
  groupId: number;
  hostname: string;
  fallbackUrl: string | null;
  enabled: boolean;
};

export type EntryDetail = {
  entry: EntrySummary;
  domain: DomainRecord;
  destinations: Destination[];
};

/** A domain plus the group it belongs to, which the entry detail omits. */
export type DomainDetail = {
  domain: DomainRecord;
  group: { id: number; name: string; notes: string };
};

export const entryKeys = {
  forDomain: (domainId: number) => ['domains', domainId, 'entries'] as const,
  one: (entryId: number) => ['entries', entryId] as const,
};

export const domainKeys = {
  one: (domainId: number) => ['domains', domainId] as const,
};

/**
 * The domain behind a URL, with its group name.
 *
 * Shared by the URL page (which needs the group for its breadcrumb) and the
 * domain settings page, so both read the same cache entry.
 */
export function useDomain(domainId: number, enabled = true) {
  return useQuery({
    queryKey: domainKeys.one(domainId),
    enabled,
    queryFn: () => apiGet<DomainDetail>(`/api/domains/${domainId}`),
  });
}

export function useEntries(domainId: number) {
  return useQuery({
    queryKey: entryKeys.forDomain(domainId),
    queryFn: () => apiGet<{ entries: EntrySummary[] }>(`/api/domains/${domainId}/entries`),
  });
}

export function useEntry(entryId: number, enabled = true) {
  return useQuery({
    queryKey: entryKeys.one(entryId),
    enabled,
    queryFn: () => apiGet<EntryDetail>(`/api/entries/${entryId}`),
  });
}

/**
 * Everything a change to one entry can invalidate.
 *
 * Deliberately wider than "this domain's entries": an edit may now move the
 * entry to another hostname, so the domain it LEAVES and the domain it JOINS
 * both have a stale listing, and only the second of those is knowable here.
 * The flat URLs listing and the liveness projection are dropped for the same
 * reason — both carry the hostname and the group this entry now shows under.
 */
function useAfterEntryChange(domainId: number) {
  const client = useQueryClient();
  return (entryId?: number) => {
    void client.invalidateQueries({ queryKey: entryKeys.forDomain(domainId) });
    void client.invalidateQueries({ queryKey: ['urls'] });
    void client.invalidateQueries({ queryKey: ['liveness'] });
    if (entryId !== undefined) void client.invalidateQueries({ queryKey: entryKeys.one(entryId) });
  };
}

export function useCreateEntry(domainId: number) {
  const after = useAfterEntryChange(domainId);
  return useMutation({
    mutationFn: (input: EntryInput) =>
      apiSend<{ entry: EntrySummary }>('POST', `/api/domains/${domainId}/entries`, input),
    onSuccess: () => after(),
  });
}

export function useUpdateEntry(domainId: number) {
  const after = useAfterEntryChange(domainId);
  return useMutation({
    mutationFn: (variables: { id: number; input: EntryUpdateInput }) =>
      apiSend<{ entry: EntrySummary }>('PATCH', `/api/entries/${variables.id}`, variables.input),
    onSuccess: (_data, variables) => after(variables.id),
  });
}

export function useDeleteEntry(domainId: number) {
  const after = useAfterEntryChange(domainId);
  return useMutation({
    mutationFn: (id: number) => apiSend<{ deleted: true }>('DELETE', `/api/entries/${id}`),
    onSuccess: () => after(),
  });
}

export function useCloneEntry(domainId: number) {
  const after = useAfterEntryChange(domainId);
  return useMutation({
    mutationFn: (variables: { id: number; path: string; label?: string }) =>
      apiSend<{ entry: EntrySummary }>('POST', `/api/entries/${variables.id}/clone`, {
        path: variables.path,
        ...(variables.label === undefined ? {} : { label: variables.label }),
      }),
    onSuccess: () => after(),
  });
}
